import React from 'react';
import { FaMapMarkerAlt, FaUsers, FaGlobe, FaBuilding } from 'react-icons/fa';

const OrganizationCard = ({ organization }) => {
  if (!organization) return null;

  const { 
    id,
    orgName,
    description,
    logoUrl,
    bannerUrl,
    city,
    state,
    country,
    website,
    services = [],
    memberCount = 0
  } = organization;

  // Get location parts that exist
  const locationParts = [city, state, country].filter(Boolean);
  const location = locationParts.length > 0 ? locationParts.join(', ') : 'Location not specified';

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
      {/* Banner Image */}
      <div className="h-24 bg-gradient-to-r from-blue-500 to-purple-600 relative">
            {bannerUrl ? (
              <img 
                src={bannerUrl} 
                alt={`${orgName} banner`} 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600"></div>
            )}
          </div>
          
          {/* Logo and Basic Info */}
          <div className="px-6 pt-2 pb-4 relative">
            <div className="flex items-start">
              {/* Logo */}
              <div className="-mt-10 mr-4">
                <div className="w-16 h-16 rounded-lg bg-white border-4 border-white shadow-md overflow-hidden">
                  {logoUrl ? (
                    <img 
                      src={logoUrl} 
                      alt={`${orgName} logo`} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <FaBuilding className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Organization Name */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {orgName || 'Organization'}
                </h3>
                
                {/* Location */}
                <div className="flex items-center text-sm text-gray-500 mt-1">
                  <FaMapMarkerAlt className="mr-1.5 flex-shrink-0" />
                  <span className="truncate">{location}</span>
                </div>
                
                {/* Member Count */}
                <div className="flex items-center text-sm text-gray-500 mt-1">
                  <FaUsers className="mr-1.5 flex-shrink-0" />
                  <span>{memberCount} {memberCount === 1 ? 'member' : 'members'}</span>
                </div>
              </div>
            </div>
            
            {/* Description */}
            {description && (
              <p className="mt-3 text-sm text-gray-600 line-clamp-3">
                {description}
              </p>
            )}
            
            {/* Services/Tags */}
            {services && services.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {services.slice(0, 3).map((service, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {service.name || service}
                  </span>
                ))}
                {services.length > 3 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    +{services.length - 3} more
                  </span>
                )}
              </div>
            )}
            
            {/* Website */}
            {website && (
              <div className="mt-3">
                <a 
                  href={website.startsWith('http') ? website : `https://${website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FaGlobe className="mr-1.5 flex-shrink-0" />
                  <span className="truncate">
                    {website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]}
                  </span>
                </a>
              </div>
            )}
          </div>
        </div>
  );
};

export default OrganizationCard;
